/**
 * FileTree Component
 *
 * Tree navigation with hierarchy:
 * - Verticals
 *   - Config Types (schema, eligibility, ranking, journey, branding)
 */

import type { ReactElement } from "react";
import { useMemo } from "react";

import type { FileTreeNode } from "../../types";

import { FileTreeItem } from "./FileTreeItem";
import { useFileTree } from "./useFileTree";

interface FileTreeProps {
  readonly searchQuery: string;
}

export function FileTree({ searchQuery }: FileTreeProps): ReactElement {
  const { tree, isLoading, error, selectedPath, expandedPaths, selectNode, toggleExpand } =
    useFileTree();

  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree;
    return filterTree(tree, searchQuery.toLowerCase());
  }, [tree, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2 px-2">
            <div className="h-4 w-4 animate-pulse rounded bg-border" />
            <div className="h-4 flex-1 animate-pulse rounded bg-border" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm text-error">Failed to load configs</p>
        <p className="mt-1 text-xs text-foreground-muted">{error}</p>
      </div>
    );
  }

  if (filteredTree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-sm text-foreground-muted">
          {searchQuery ? "No matching configs" : "No configs found"}
        </p>
        {searchQuery && (
          <p className="mt-1 text-xs text-foreground-muted">Try a different search term</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col" role="tree">
      {filteredTree.map((node) => (
        <FileTreeItem
          key={node.id}
          node={node}
          depth={0}
          isSelected={selectedPath === node.path}
          isExpanded={expandedPaths.has(node.path)}
          onSelect={selectNode}
          onToggle={toggleExpand}
        />
      ))}
    </div>
  );
}

function filterTree(nodes: readonly FileTreeNode[], query: string): readonly FileTreeNode[] {
  const result: FileTreeNode[] = [];

  for (const node of nodes) {
    if (node.name.toLowerCase().includes(query)) {
      result.push(node);
      continue;
    }

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTree(node.children, query);
      if (filteredChildren.length > 0) {
        result.push({
          ...node,
          children: filteredChildren,
        });
      }
    }
  }

  return result;
}
